import React, { createContext, useContext, useState, useEffect } from 'react';
import { Agent, SelectedAgent } from '../types/agent';
import { agents } from '../constants/agents';

interface AgentContextType {
  mainAgent: Agent;
  selectedAgents: SelectedAgent[];
  addAgent: (agent: Agent) => void;
  removeAgent: (agentId: string) => void;
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const AgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const defaultMainAgent = agents.find(agent => agent.selected) || agents[0];
  const [mainAgent, setMainAgent] = useState<Agent>(defaultMainAgent);
  const [selectedAgents, setSelectedAgents] = useState<SelectedAgent[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const addAgent = (agent: Agent) => {
    // Don't add if already in the list or if it's the main agent
    if (selectedAgents.some(a => a.id === agent.id) || agent.id === mainAgent.id) {
      return;
    }
    
    // Create the updated array once and use it for both state update and localStorage
    const updatedAgents = [...selectedAgents, { ...agent, isMain: false }];
    
    // Update state
    setSelectedAgents(updatedAgents);
    
    // Store the selected agents in localStorage with the same updated array
    localStorage.setItem('selectedAgentIds', JSON.stringify(updatedAgents.map(a => a.id)));
    
    console.log('Agent added:', agent.id);
    console.log('Updated selected agents:', updatedAgents.map(a => a.id));
  };

  const removeAgent = (agentId: string) => {
    // Create the filtered array once and use it for both state update and localStorage
    const updatedAgents = selectedAgents.filter(agent => agent.id !== agentId);
    
    // Update state
    setSelectedAgents(updatedAgents);
    
    // Update localStorage with the same filtered array
    localStorage.setItem('selectedAgentIds', JSON.stringify(updatedAgents.map(a => a.id)));
    
    console.log('Agent removed:', agentId);
    console.log('Updated selected agents:', updatedAgents.map(a => a.id));
  };

  // Load the main agent and selected agents from localStorage on initial render
  useEffect(() => {
    const savedMainAgentId = localStorage.getItem('mainAgentId');
    if (savedMainAgentId) {
      const agent = agents.find(a => a.id === savedMainAgentId && a.enabled);
      if (agent) {
        setMainAgent(agent);
      }
    }

    const savedAgentIds = localStorage.getItem('selectedAgentIds');
    if (savedAgentIds) {
      try {
        const agentIds = JSON.parse(savedAgentIds) as string[];
        const savedAgents = agentIds
          .map(id => agents.find(a => a.id === id && a.enabled))
          .filter((agent): agent is Agent => agent !== undefined)
          .map(agent => ({ ...agent, isMain: false }));
        
        setSelectedAgents(savedAgents);
      } catch (error) {
        console.error('Failed to parse selected agents from localStorage', error);
      }
    }
  }, []);

  return (
    <AgentContext.Provider
      value={{
        mainAgent,
        selectedAgents,
        addAgent,
        removeAgent,
        isDialogOpen,
        openDialog,
        closeDialog
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = (): AgentContextType => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
